/**
 * Precise metronome using the Web Audio API look-ahead scheduler
 * (avoids setInterval drift / main-thread lag).
 *
 * BPM range: 40–240. Time signatures: 4/4, 3/4, 2/4, 6/8.
 */

export type TimeSignatureId = "4/4" | "3/4" | "2/4" | "6/8";

export interface TimeSignatureSpec {
  id: TimeSignatureId;
  /** Number of click pulses per bar. */
  pulsesPerBar: number;
  /**
   * Pulse indices (0-based) that are strong downbeats.
   * Remaining pulses are weaker (upbeats / subdivisions).
   */
  accentPulses: number[];
  /**
   * How BPM maps to pulse spacing:
   * - "quarter": secondsPerPulse = 60 / bpm  (used for 4/4, 3/4, 2/4)
   * - "dottedQuarter": one dotted-quarter = 60/bpm, split into 3 eighth pulses (6/8)
   */
  bpmUnit: "quarter" | "dottedQuarter";
}

export const TIME_SIGNATURES: Record<TimeSignatureId, TimeSignatureSpec> = {
  "4/4": { id: "4/4", pulsesPerBar: 4, accentPulses: [0], bpmUnit: "quarter" },
  "3/4": { id: "3/4", pulsesPerBar: 3, accentPulses: [0], bpmUnit: "quarter" },
  "2/4": { id: "2/4", pulsesPerBar: 2, accentPulses: [0], bpmUnit: "quarter" },
  "6/8": { id: "6/8", pulsesPerBar: 6, accentPulses: [0, 3], bpmUnit: "dottedQuarter" },
};

export interface MetronomeBeatEvent {
  /** AudioContext time when the click sounds. */
  audioTime: number;
  barIndex: number;
  /** 0-based pulse within the bar. */
  pulseIndex: number;
  isDownbeat: boolean;
  isAccent: boolean;
  bpm: number;
  timeSignature: TimeSignatureId;
}

export type MetronomeBeatHandler = (event: MetronomeBeatEvent) => void;

export interface MetronomeConfig {
  bpm?: number;
  timeSignature?: TimeSignatureId;
  volume?: number;
  /** Schedule window ahead of the playhead (seconds). */
  scheduleAhead?: number;
  /** Scheduler poll interval (ms). */
  lookaheadMs?: number;
}

const MIN_BPM = 40;
const MAX_BPM = 240;

export function clampBpm(bpm: number): number {
  return Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(bpm)));
}

export function secondsPerPulse(bpm: number, sig: TimeSignatureSpec): number {
  const b = clampBpm(bpm);
  if (sig.bpmUnit === "dottedQuarter") {
    // Dotted-quarter = 60/bpm; each of 3 eighths in that beat
    return 60 / b / 3;
  }
  return 60 / b;
}

export class MetronomeEngine {
  private ctx: AudioContext | null = null;
  private clickGain: GainNode | null = null;
  private timerId: ReturnType<typeof setInterval> | null = null;
  private nextPulseTime = 0;
  private pulseIndex = 0;
  private barIndex = 0;
  private running = false;
  private bpm = 100;
  private timeSignature: TimeSignatureId = "4/4";
  private volume = 0.55;
  private scheduleAhead = 0.12;
  private lookaheadMs = 25;
  private beatHandlers = new Set<MetronomeBeatHandler>();
  /** Pending UI beat notification (coalesced). */
  private pendingBeat: MetronomeBeatEvent | null = null;
  private beatUiRaf: number | null = null;
  private recentBeats: MetronomeBeatEvent[] = [];
  private readonly recentCap = 64;

  get isRunning(): boolean {
    return this.running;
  }

  getBpm(): number {
    return this.bpm;
  }

  getTimeSignature(): TimeSignatureId {
    return this.timeSignature;
  }

  getVolume(): number {
    return this.volume;
  }

  /** Must share the unlocked tutor AudioContext. */
  attach(ctx: AudioContext): void {
    this.ctx = ctx;
    if (!this.clickGain) {
      this.clickGain = ctx.createGain();
      this.clickGain.gain.value = this.volume;
      this.clickGain.connect(ctx.destination);
    }
  }

  onBeat(handler: MetronomeBeatHandler): () => void {
    this.beatHandlers.add(handler);
    return () => this.beatHandlers.delete(handler);
  }

  setBpm(bpm: number): void {
    this.bpm = clampBpm(bpm);
  }

  setTimeSignature(id: TimeSignatureId): void {
    if (!(id in TIME_SIGNATURES)) return;
    this.timeSignature = id;
    this.pulseIndex = 0;
  }

  setVolume(volume01: number): void {
    this.volume = Math.max(0, Math.min(1, volume01));
    if (this.clickGain && this.ctx) {
      this.clickGain.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.02);
    }
  }

  configure(config: MetronomeConfig): void {
    if (config.bpm != null) this.setBpm(config.bpm);
    if (config.timeSignature != null) this.setTimeSignature(config.timeSignature);
    if (config.volume != null) this.setVolume(config.volume);
    if (config.scheduleAhead != null) this.scheduleAhead = config.scheduleAhead;
    if (config.lookaheadMs != null) this.lookaheadMs = config.lookaheadMs;
  }

  start(): void {
    if (!this.ctx || !this.clickGain) {
      throw new Error("MetronomeEngine.attach(audioContext) before start()");
    }
    if (this.running) return;
    if (this.ctx.state === "suspended") {
      void this.ctx.resume();
    }

    this.running = true;
    this.pulseIndex = 0;
    this.nextPulseTime = this.ctx.currentTime + 0.06;
    this.timerId = setInterval(() => this.schedulerTick(), this.lookaheadMs);
    this.schedulerTick();
  }

  stop(): void {
    this.running = false;
    if (this.timerId != null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    if (this.beatUiRaf != null) {
      cancelAnimationFrame(this.beatUiRaf);
      this.beatUiRaf = null;
    }
    this.pendingBeat = null;
  }

  /** Reset bar counter but keep BPM / signature. */
  resetTransport(): void {
    this.pulseIndex = 0;
    this.barIndex = 0;
    this.recentBeats = [];
    if (this.ctx && this.running) {
      this.nextPulseTime = this.ctx.currentTime + 0.06;
    }
  }

  getSecondsPerPulse(): number {
    return secondsPerPulse(this.bpm, TIME_SIGNATURES[this.timeSignature]);
  }

  /**
   * Beats scheduled near `audioTime` (for grading). Includes a small past window.
   */
  getRecentBeats(aroundAudioTime?: number): MetronomeBeatEvent[] {
    if (aroundAudioTime == null) return [...this.recentBeats];
    const window = this.getSecondsPerPulse() * 2;
    return this.recentBeats.filter(
      (b) => Math.abs(b.audioTime - aroundAudioTime) <= window,
    );
  }

  private schedulerTick(): void {
    if (!this.ctx || !this.running) return;
    const horizon = this.ctx.currentTime + this.scheduleAhead;
    while (this.nextPulseTime < horizon) {
      this.schedulePulse(this.nextPulseTime, this.pulseIndex, this.barIndex);
      this.advancePulse();
    }
  }

  private advancePulse(): void {
    const spec = TIME_SIGNATURES[this.timeSignature];
    this.nextPulseTime += secondsPerPulse(this.bpm, spec);
    this.pulseIndex += 1;
    if (this.pulseIndex >= spec.pulsesPerBar) {
      this.pulseIndex = 0;
      this.barIndex += 1;
    }
  }

  private schedulePulse(time: number, pulseIndex: number, barIndex: number): void {
    if (!this.ctx || !this.clickGain) return;
    const spec = TIME_SIGNATURES[this.timeSignature];
    const isAccent = spec.accentPulses.includes(pulseIndex);
    const isDownbeat = pulseIndex === 0;

    this.playClick(time, isAccent);

    const event: MetronomeBeatEvent = {
      audioTime: time,
      barIndex,
      pulseIndex,
      isDownbeat,
      isAccent,
      bpm: this.bpm,
      timeSignature: this.timeSignature,
    };

    this.recentBeats.push(event);
    if (this.recentBeats.length > this.recentCap) {
      this.recentBeats.splice(0, this.recentBeats.length - this.recentCap);
    }

    // Coalesce UI notifications to one rAF — never flood setTimeout/main thread.
    this.pendingBeat = event;
    if (this.beatUiRaf == null) {
      this.beatUiRaf = requestAnimationFrame(() => {
        this.beatUiRaf = null;
        const e = this.pendingBeat;
        this.pendingBeat = null;
        if (!e) return;
        for (const h of this.beatHandlers) h(e);
      });
    }
  }

  private playClick(time: number, accent: boolean): void {
    if (!this.ctx || !this.clickGain) return;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(accent ? 1400 : 980, time);

    const peak = accent ? 0.9 : 0.45;
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(peak, time + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + (accent ? 0.055 : 0.035));

    osc.connect(gain);
    gain.connect(this.clickGain);
    osc.start(time);
    osc.stop(time + 0.08);
  }
}
